import React, { useState, useEffect } from 'react';
import { CreditCard, X, Shield, Lock, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPaymentGateways } from '../api/pricing';

const loadPaystackScript = () =>
  new Promise((resolve, reject) => {
    if (window.PaystackPop) return resolve(window.PaystackPop);
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => resolve(window.PaystackPop);
    script.onerror = () => reject(new Error('Failed to load Paystack script'));
    document.body.appendChild(script);
  });

const PaymentModal = ({ isOpen, onClose, onSuccess, propertyData, bookingData }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [gateways, setGateways] = useState({});
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [selectedGateway, setSelectedGateway] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetch = async () => {
      setGatewaysLoading(true);
      try {
        const data = await getPaymentGateways();
        setGateways(data);
        // Auto-select: if only one enabled pick it, else let user choose
        const enabled = Object.entries(data).filter(([, cfg]) => cfg.enabled);
        if (enabled.length === 1) setSelectedGateway(enabled[0][0]);
        else setSelectedGateway(null);
      } catch {
        setGateways({});
      } finally {
        setGatewaysLoading(false);
      }
    };
    fetch();
  }, [isOpen]);

  const handleBookingAndVerify = async (reference, gateway) => {
    const { createBooking } = await import('../api/bookings');
    const booking = await createBooking({
      propertyId: propertyData.id,
      checkInDate: bookingData.checkIn,
      checkOutDate: bookingData.checkOut,
      numberOfGuests: parseInt(bookingData.guests) || 1,
      totalAmount: bookingData.total,
      status: 'pending',
      paymentStatus: 'pending',
    });

    const { verifyPayment } = await import('../api/bookings');
    const verifyResponse = await verifyPayment(booking.id, reference, gateway);

    if (verifyResponse.success) {
      if (onSuccess) onSuccess(verifyResponse.booking);
      onClose();
    } else {
      setErrors({ submit: 'Payment verification failed. Please contact support.' });
    }
  };

  const handlePaystack = async () => {
    setIsLoading(true);
    setErrors({});
    try {
      const cfg = gateways.paystack;
      const publicKey = cfg?.publicKey;
      if (!publicKey) throw new Error('Paystack public key not configured');

      const PaystackPop = await loadPaystackScript();
      const amount = Math.round(Number(bookingData?.total || 0) * 100);

      const handler = PaystackPop.setup({
        key: publicKey,
        email: user?.email || 'guest@example.com',
        amount,
        currency: 'NGN',
        ref: `ROAR-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        callback: function(response) {
          handleBookingAndVerify(response.reference, 'paystack')
            .catch(function(err) {
              setErrors({ submit: 'Payment successful but booking failed: ' + err.message });
            })
            .finally(function() {
              setIsLoading(false);
            });
        },
        onClose: function() {
          setIsLoading(false);
        },
      });
      handler.openIframe();
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to initialize payment.' });
      setIsLoading(false);
    }
  };

  const handleMoments = async () => {
    setIsLoading(true);
    setErrors({});
    try {
      const cfg = gateways.moments;
      if (!cfg?.publicKey) throw new Error('Moments not configured');

      // Moments React SDK uses a popup similar to Paystack.
      // Load Moments SDK dynamically
      if (!window.MomentsPop) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://js.momentco.io/v1/inline.js';
          s.async = true;
          s.onload = res;
          s.onerror = () => rej(new Error('Failed to load Moments SDK'));
          document.body.appendChild(s);
        });
      }

      const reference = `ROAR-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const amount = Math.round(Number(bookingData?.total || 0) * 100);

      const handler = window.MomentsPop.setup({
        key: cfg.publicKey,
        email: user?.email || 'guest@example.com',
        amount,
        currency: 'NGN',
        reference,
        callback: function(response) {
          handleBookingAndVerify(response.reference || reference, 'moments')
            .catch(function(err) {
              setErrors({ submit: 'Payment successful but booking failed: ' + err.message });
            })
            .finally(function() {
              setIsLoading(false);
            });
        },
        onClose: function() {
          setIsLoading(false);
        },
      });
      handler.openIframe();
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to initialize Moments payment.' });
      setIsLoading(false);
    }
  };

  const handlePay = () => {
    if (selectedGateway === 'paystack') handlePaystack();
    else if (selectedGateway === 'moments') handleMoments();
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  const total = Number(bookingData?.total) || 0;
  const enabledGateways = Object.entries(gateways).filter(([, cfg]) => cfg.enabled);
  const noneEnabled = !gatewaysLoading && enabledGateways.length === 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Complete Payment</h2>
              <p className="text-gray-600 mt-1">Secure payment processing</p>
            </div>
            <button onClick={handleClose} disabled={isLoading} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Booking Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Property:</span><span className="font-medium">{propertyData?.title}</span></div>
              {bookingData?.appliedRule && (
                <div className="flex justify-between text-pink-600"><span>Custom rate:</span><span className="font-medium">{bookingData.appliedRule.label}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-600">Check-in:</span><span className="font-medium">{bookingData?.checkIn}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Check-out:</span><span className="font-medium">{bookingData?.checkOut}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Nights:</span><span className="font-medium">{bookingData?.nights}</span></div>
              <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                <span>Total:</span><span>₦{total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Security badge */}
          <div className="flex items-center justify-center text-green-600 gap-2">
            <Shield className="w-4 h-4" /><Lock className="w-4 h-4" />
            <span className="text-sm font-medium">SSL Encrypted Payment</span>
          </div>

          {/* Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{errors.submit}</div>
          )}

          {gatewaysLoading ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading payment options…</div>
          ) : noneEnabled ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 text-center">
              Payment is currently unavailable. Please contact support.
            </div>
          ) : (
            <>
              {/* Gateway selection (only shown when both enabled) */}
              {enabledGateways.length > 1 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Choose payment method:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {enabledGateways.map(([gw, cfg]) => (
                      <button
                        key={gw}
                        type="button"
                        onClick={() => setSelectedGateway(gw)}
                        className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${selectedGateway === gw ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        {gw === 'paystack' ? (
                          <>
                            <CreditCard className="w-5 h-5 text-blue-500" />
                            <span className="text-xs font-bold">Paystack</span>
                          </>
                        ) : (
                          <>
                            <Zap className="w-5 h-5 text-orange-500" />
                            <span className="text-xs font-bold">Moments</span>
                          </>
                        )}
                        <span className={`text-[10px] px-1 rounded font-medium ${cfg.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {cfg.mode}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pay button */}
              <button
                type="button"
                onClick={handlePay}
                disabled={isLoading || !selectedGateway}
                className="w-full py-4 rounded-lg font-bold text-white text-lg bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing…
                  </span>
                ) : !selectedGateway ? (
                  'Select a payment method'
                ) : (
                  `Pay ₦${total.toLocaleString()} with ${selectedGateway === 'paystack' ? 'Paystack' : 'Moments'}`
                )}
              </button>

              <div className="text-center text-xs text-gray-500">
                {selectedGateway === 'paystack' && 'You will be redirected to Paystack\'s secure checkout.'}
                {selectedGateway === 'moments' && 'You will be redirected to Moments\' secure checkout.'}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
